import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, client, IP_DENIED_EVENT, type SessionUser } from './api';

type AuthState = {
  user: SessionUser | null;
  /** Not signed in, but browsing from an allowed network. */
  guest: boolean;
  /** Set when this network is blocked by the IP allowlist; holds the visitor's IP. */
  denied: { ip: string } | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<SessionUser>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [guest, setGuest] = useState(false);
  const [denied, setDenied] = useState<{ ip: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: me, guest: g } = await client.me();
      setUser(me);
      setGuest(Boolean(g) && !me);
      setDenied(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ip_denied') {
        setDenied({ ip: String(err.data?.ip ?? '') });
      }
      setUser(null);
      setGuest(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Any API call can report that this network was blocked (e.g. an admin turned restriction on).
  useEffect(() => {
    const onDenied = (e: Event) => {
      const ip = (e as CustomEvent<{ ip?: string }>).detail?.ip ?? '';
      setDenied({ ip });
    };
    window.addEventListener(IP_DENIED_EVENT, onDenied);
    return () => window.removeEventListener(IP_DENIED_EVENT, onDenied);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user: me } = await client.login(email, password);
    setUser(me);
    setGuest(false);
    return me;
  }, []);

  const logout = useCallback(async () => {
    await client.logout();
    setUser(null);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, guest, denied, loading, refresh, login, logout }),
    [user, guest, denied, loading, refresh, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
